'use client'

import {
    appLogin,
    registerClient,
    resendClientOtp,
    verifyClientOtp,
} from '@/lib/api/auth/auth'
import { setCredentials } from '@/store/slices/authSlice'
import { useAppDispatch, useAuth } from '@/store/hooks'
import { useEffect, useState } from 'react'
import {
    MdArrowBack,
    MdBadge,
    MdLockOutline,
    MdMailOutline,
    MdPerson,
    MdPhone,
} from 'react-icons/md'
import { Navigate, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

/** Etapas da tela: entrar, criar conta, confirmar o código do e-mail. */
type Step = 'login' | 'register' | 'otp'

const OTP_LENGTH = 6
const RESEND_COOLDOWN_SEC = 60

const onlyDigits = (value: string): string => value.replace(/\D/g, '')

/** 000.000.000-00 — mesma máscara do cadastro de cliente no painel. */
const formatCpf = (value: string): string => {
    const n = onlyDigits(value).slice(0, 11)
    if (n.length <= 3) return n
    if (n.length <= 6) return `${n.slice(0, 3)}.${n.slice(3)}`
    if (n.length <= 9) return `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6)}`
    return `${n.slice(0, 3)}.${n.slice(3, 6)}.${n.slice(6, 9)}-${n.slice(9, 11)}`
}

/** (00) 00000-0000 */
const formatPhone = (value: string): string => {
    const n = onlyDigits(value).slice(0, 11)
    if (n.length <= 2) return n
    if (n.length <= 7) return `(${n.slice(0, 2)}) ${n.slice(2)}`
    if (n.length <= 10)
        return `(${n.slice(0, 2)}) ${n.slice(2, 6)}-${n.slice(6)}`
    return `(${n.slice(0, 2)}) ${n.slice(2, 7)}-${n.slice(7)}`
}

/** Input arredondado, macio — a cara do portal do paciente (não do painel). */
const INPUT_CLASS =
    'w-full h-[52px] pl-12 pr-4 rounded-2xl border border-base-200 bg-base-100 text-[15px] text-base-content placeholder:text-base-content/40 outline-none transition-colors duration-200 focus:border-secondary focus:ring-4 focus:ring-secondary/12'

const PRIMARY_BTN =
    'w-full h-[52px] rounded-2xl bg-secondary text-primary-content font-semibold text-[15px] transition-all duration-300 hover:brightness-[1.05] hover:shadow-lg hover:shadow-secondary/25 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:shadow-none'

export default function ClientLoginPage() {
    const navigate = useNavigate()
    const dispatch = useAppDispatch()
    const { isAuthenticated, user } = useAuth()

    const [step, setStep] = useState<Step>('login')
    const [loading, setLoading] = useState(false)

    const [email, setEmail] = useState('')
    const [password, setPassword] = useState('')

    const [name, setName] = useState('')
    const [cpf, setCpf] = useState('')
    const [phone, setPhone] = useState('')
    const [confirmPassword, setConfirmPassword] = useState('')

    const [code, setCode] = useState('')
    const [cooldown, setCooldown] = useState(0)

    useEffect(() => {
        if (cooldown <= 0) return
        const timer = setTimeout(() => setCooldown(c => c - 1), 1000)
        return () => clearTimeout(timer)
    }, [cooldown])

    // Já logado como cliente: vai direto para os atendimentos.
    if (isAuthenticated && user?.userType === 'CLIENT') {
        return <Navigate to="/client/appointments" replace />
    }

    /** Grava a sessão e entra no portal. Mesma sessão do painel/app. */
    const enterPortal = (result: any) => {
        dispatch(
            setCredentials({
                user: result.user,
                token: result.access_token,
                refreshToken: result.refresh_token,
                accessibleCompanies: result.accessibleCompanies,
            } as any)
        )
        navigate('/client/appointments', { replace: true })
    }

    const handleLogin = async (e: React.FormEvent) => {
        e.preventDefault()
        if (!email.trim() || !password) return

        setLoading(true)
        try {
            // `auth/login` recusa CLIENT (é a porta do painel); o portal usa o
            // mesmo endpoint do app.
            const res = await appLogin(email.trim().toLowerCase(), password)
            if (res.success && res.result?.access_token) {
                enterPortal(res.result)
            } else {
                toast.error(res.error || 'E-mail ou senha inválidos.')
            }
        } catch {
            toast.error('Não foi possível entrar. Tente novamente.')
        } finally {
            setLoading(false)
        }
    }

    const handleRegister = async (e: React.FormEvent) => {
        e.preventDefault()

        if (name.trim().length < 2) {
            toast.error('Informe seu nome completo.')
            return
        }
        if (onlyDigits(cpf).length !== 11) {
            toast.error('Informe um CPF válido.')
            return
        }
        if (password.length < 6) {
            toast.error('A senha deve ter ao menos 6 caracteres.')
            return
        }
        if (password !== confirmPassword) {
            toast.error('As senhas não conferem.')
            return
        }

        setLoading(true)
        try {
            const res = await registerClient({
                name: name.trim(),
                email: email.trim().toLowerCase(),
                password,
                cpf: onlyDigits(cpf),
                phone: phone ? onlyDigits(phone) : undefined,
                // Exige que o CPF já exista como paciente: o portal só serve para
                // acompanhar atendimentos (ver ClientRegisterDto.channel).
                channel: 'WEB_PORTAL',
            })

            if (res.success && res.result?.requiresVerification) {
                setStep('otp')
                setCooldown(RESEND_COOLDOWN_SEC)
                toast.info(
                    'Enviamos um código de confirmação para o seu e-mail.'
                )
            } else {
                toast.error(
                    res.error ||
                        'Não foi possível criar a conta. Confira os dados informados.'
                )
            }
        } catch {
            toast.error('Não foi possível criar a conta. Tente novamente.')
        } finally {
            setLoading(false)
        }
    }

    const handleVerifyOtp = async (e: React.FormEvent) => {
        e.preventDefault()
        if (code.length !== OTP_LENGTH) return

        setLoading(true)
        try {
            const res = await verifyClientOtp(email.trim().toLowerCase(), code)
            if (res.success && res.result?.access_token) {
                enterPortal(res.result)
            } else {
                toast.error(res.error || 'Código inválido ou expirado.')
            }
        } catch {
            toast.error('Não foi possível confirmar o código.')
        } finally {
            setLoading(false)
        }
    }

    const handleResend = async () => {
        if (cooldown > 0) return
        setLoading(true)
        try {
            await resendClientOtp(email.trim().toLowerCase())
            setCooldown(RESEND_COOLDOWN_SEC)
            toast.info('Código reenviado.')
        } catch {
            toast.error('Não foi possível reenviar o código.')
        } finally {
            setLoading(false)
        }
    }

    return (
        <div
            className="min-h-screen flex flex-col relative overflow-hidden bg-gradient-to-b from-secondary/8 via-base-100 to-base-100"
            data-theme="atendimento"
        >
            {/* Blobs suaves — clima calmo de app de saúde, sem gradiente forte. */}
            <div className="pointer-events-none absolute -top-32 -right-24 w-[26rem] h-[26rem] rounded-full bg-secondary/15 blur-3xl" />
            <div className="pointer-events-none absolute top-1/4 -left-28 w-80 h-80 rounded-full bg-accent/15 blur-3xl" />

            <div className="flex-1 flex items-center justify-center p-4 relative z-10">
                <div className="w-full max-w-md animate-fade-in-up">
                    {/* Marca */}
                    <div className="flex flex-col items-center mb-6">
                        <div className="w-20 h-20 rounded-3xl bg-base-100 elevation-2 flex items-center justify-center mb-3">
                            <img
                                src="/logo512.png"
                                alt="Mediar Saúde"
                                className="w-12 h-12 object-contain"
                            />
                        </div>
                        <h1 className="text-2xl font-bold text-base-content tracking-tight">
                            Mediar Saúde
                        </h1>
                        <p className="text-sm text-base-content/50">
                            Área do cliente
                        </p>
                    </div>

                    <div className="bg-base-100 rounded-3xl elevation-2 border border-base-200/60 p-7">
                        {step === 'login' && (
                            <>
                                <h2 className="text-xl font-bold text-base-content">
                                    Acesse sua área
                                </h2>
                                <p className="text-sm text-base-content/50 mt-1 mb-6">
                                    Acompanhe seus atendimentos e pagamentos.
                                </p>
                                <form
                                    onSubmit={handleLogin}
                                    className="flex flex-col gap-4"
                                >
                                    <div className="relative">
                                        <MdMailOutline className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-base-content/40 pointer-events-none" />
                                        <input
                                            type="email"
                                            className={INPUT_CLASS}
                                            value={email}
                                            onChange={e =>
                                                setEmail(e.target.value)
                                            }
                                            placeholder="seu@email.com"
                                            autoComplete="email"
                                        />
                                    </div>
                                    <div className="relative">
                                        <MdLockOutline className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-base-content/40 pointer-events-none" />
                                        <input
                                            type="password"
                                            className={INPUT_CLASS}
                                            value={password}
                                            onChange={e =>
                                                setPassword(e.target.value)
                                            }
                                            placeholder="Sua senha"
                                            autoComplete="current-password"
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className={`${PRIMARY_BTN} mt-1`}
                                        disabled={loading}
                                    >
                                        {loading ? 'Entrando...' : 'Entrar'}
                                    </button>
                                </form>

                                <div className="flex items-center gap-3 my-5">
                                    <div className="flex-1 h-px bg-base-200" />
                                    <span className="text-xs text-base-content/40">
                                        ou
                                    </span>
                                    <div className="flex-1 h-px bg-base-200" />
                                </div>

                                <button
                                    type="button"
                                    className="w-full h-12 rounded-2xl border border-base-200 font-semibold text-sm text-base-content hover:border-secondary/40 hover:bg-secondary/5 transition-all"
                                    onClick={() => setStep('register')}
                                >
                                    Criar conta
                                </button>
                            </>
                        )}

                        {step === 'register' && (
                            <>
                                <div className="flex items-center gap-2 mb-1">
                                    <button
                                        type="button"
                                        className="w-8 h-8 rounded-full flex items-center justify-center text-base-content/50 hover:bg-base-200 transition-colors"
                                        onClick={() => setStep('login')}
                                        aria-label="Voltar"
                                    >
                                        <MdArrowBack className="w-5 h-5" />
                                    </button>
                                    <h2 className="text-xl font-bold text-base-content">
                                        Criar conta
                                    </h2>
                                </div>
                                <p className="text-sm text-base-content/50 mb-6 pl-10">
                                    Use o CPF do seu cadastro de paciente. Se
                                    ainda não é nosso paciente, procure a
                                    recepção.
                                </p>
                                <form
                                    onSubmit={handleRegister}
                                    className="flex flex-col gap-4"
                                >
                                    <div className="relative">
                                        <MdPerson className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-base-content/40 pointer-events-none" />
                                        <input
                                            type="text"
                                            className={INPUT_CLASS}
                                            value={name}
                                            onChange={e =>
                                                setName(e.target.value)
                                            }
                                            placeholder="Nome completo"
                                        />
                                    </div>
                                    <div className="relative">
                                        <MdBadge className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-base-content/40 pointer-events-none" />
                                        <input
                                            type="text"
                                            className={INPUT_CLASS}
                                            value={cpf}
                                            onChange={e =>
                                                setCpf(
                                                    formatCpf(e.target.value)
                                                )
                                            }
                                            placeholder="CPF"
                                            inputMode="numeric"
                                        />
                                    </div>
                                    <div className="relative">
                                        <MdMailOutline className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-base-content/40 pointer-events-none" />
                                        <input
                                            type="email"
                                            className={INPUT_CLASS}
                                            value={email}
                                            onChange={e =>
                                                setEmail(e.target.value)
                                            }
                                            placeholder="E-mail"
                                        />
                                    </div>
                                    <div className="relative">
                                        <MdPhone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-base-content/40 pointer-events-none" />
                                        <input
                                            type="text"
                                            className={INPUT_CLASS}
                                            value={phone}
                                            onChange={e =>
                                                setPhone(
                                                    formatPhone(e.target.value)
                                                )
                                            }
                                            placeholder="Celular (opcional)"
                                            inputMode="numeric"
                                        />
                                    </div>
                                    <div className="relative">
                                        <MdLockOutline className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-base-content/40 pointer-events-none" />
                                        <input
                                            type="password"
                                            className={INPUT_CLASS}
                                            value={password}
                                            onChange={e =>
                                                setPassword(e.target.value)
                                            }
                                            placeholder="Senha (mín. 6 caracteres)"
                                            autoComplete="new-password"
                                        />
                                    </div>
                                    <div className="relative">
                                        <MdLockOutline className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-base-content/40 pointer-events-none" />
                                        <input
                                            type="password"
                                            className={INPUT_CLASS}
                                            value={confirmPassword}
                                            onChange={e =>
                                                setConfirmPassword(
                                                    e.target.value
                                                )
                                            }
                                            placeholder="Confirmar senha"
                                            autoComplete="new-password"
                                        />
                                    </div>
                                    <button
                                        type="submit"
                                        className={`${PRIMARY_BTN} mt-1`}
                                        disabled={loading}
                                    >
                                        {loading ? 'Criando...' : 'Criar conta'}
                                    </button>
                                </form>
                            </>
                        )}

                        {step === 'otp' && (
                            <>
                                <h2 className="text-xl font-bold text-base-content">
                                    Confirme seu e-mail
                                </h2>
                                <p className="text-sm text-base-content/50 mt-1 mb-6">
                                    Digite o código de {OTP_LENGTH} dígitos
                                    enviado para{' '}
                                    <strong className="text-base-content/80">
                                        {email}
                                    </strong>
                                    .
                                </p>
                                <form
                                    onSubmit={handleVerifyOtp}
                                    className="flex flex-col gap-4"
                                >
                                    <input
                                        type="text"
                                        className="w-full h-16 rounded-2xl border border-base-200 bg-base-100 text-center text-3xl tracking-[0.4em] font-bold text-base-content outline-none transition-colors focus:border-secondary focus:ring-4 focus:ring-secondary/12"
                                        value={code}
                                        onChange={e =>
                                            setCode(
                                                e.target.value
                                                    .replace(/\D/g, '')
                                                    .slice(0, OTP_LENGTH)
                                            )
                                        }
                                        placeholder="000000"
                                        inputMode="numeric"
                                        autoFocus
                                    />
                                    <button
                                        type="submit"
                                        className={PRIMARY_BTN}
                                        disabled={
                                            loading ||
                                            code.length !== OTP_LENGTH
                                        }
                                    >
                                        {loading
                                            ? 'Confirmando...'
                                            : 'Confirmar'}
                                    </button>
                                </form>
                                <button
                                    type="button"
                                    className="w-full h-11 mt-2 rounded-2xl text-sm font-medium text-secondary hover:bg-secondary/5 transition-colors disabled:text-base-content/30 disabled:hover:bg-transparent"
                                    onClick={handleResend}
                                    disabled={loading || cooldown > 0}
                                >
                                    {cooldown > 0
                                        ? `Reenviar código em ${cooldown}s`
                                        : 'Reenviar código'}
                                </button>
                            </>
                        )}
                    </div>

                    <p className="text-center text-xs text-base-content/40 mt-6">
                        © {new Date().getFullYear()} Mediar Saúde
                    </p>
                </div>
            </div>
        </div>
    )
}
